// UI language store. Deliberately mirrors src/lib/theme.ts — same shape, same
// lifecycle — rather than pulling in i18next-browser-languagedetector, so the app
// has ONE convention for a persisted UI preference: seed from localStorage → browser
// → default, apply before React renders, expose a subscription hook.
//
// NOTE: this is the language of the INTERFACE. It is unrelated to the "Spoken
// language" field on the Launch card, which controls the accent the characters speak
// in the finished film.
export type Lang = 'en' | 'es';

export const LANGUAGES: { code: Lang; label: string; short: string }[] = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'es', label: 'Español', short: 'ES' },
];

const KEY = 'wr_ui_lang'; // same convention as the theme key

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'en' || saved === 'es') return saved;
    if (navigator.language?.toLowerCase().startsWith('es')) return 'es';
  } catch {
    /* SSR/no-storage — fall through */
  }
  return 'en';
}

let current: Lang = initialLang();
const listeners = new Set<(l: Lang) => void>();

function apply(l: Lang): void {
  document.documentElement.lang = l;
}

// Apply at module load, before React renders.
apply(current);

export function getLang(): Lang {
  return current;
}

export function setLang(l: Lang): void {
  current = l;
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* ignore */
  }
  apply(l);
  listeners.forEach((fn) => fn(l));
}

export function onLangChange(fn: (l: Lang) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// React hook so components re-render on change.
import { useEffect, useState } from 'react';
export function useLang(): Lang {
  const [l, setL] = useState<Lang>(current);
  useEffect(() => onLangChange(setL), []);
  return l;
}
